/**
 * VariableResolver
 *
 * Finds variables available for route conditions by traversing
 * predecessor screens and matching them against FunnelVariable.boundScreenIds.
 *
 * Uses BFS on V2 FlowDocument edges (content.actionBar.primary.action.edges, etc.).
 *
 * @module v2-variable-utils
 */

import type { FlowDocument, StepNode } from '@segmently/schema-models/v2';
import type { BoundVariable } from '../types/variable-utils.types';

/**
 * V2 edge locations within StepNode.content where navigation edges can be found.
 * Each path leads to an array of Edge objects with nextScreenId.
 * Must stay in sync with V2TransitionManager.ACTION_KEY_TO_CONTENT_PATH.
 */
const V2_EDGE_PATHS: readonly string[] = [
  'actionBar.primary.action.edges',
  'actionBar.secondary.action.edges',
  'actionBar.purchase.action.edges',
  'options.items',                      // items[].action.edges
  'carousel.timer.action.edges',
  'stepper.timer.action.edges',
  'countdown.action.edges',
  'systemPrompt.action.edges',
  'header.skip.action.edges',
  'header.back.action.edges',
  'header.close.action.edges',
  'embed.callback.action.edges',
];

export class VariableResolver {
  /**
   * Find all variables that have been bound by predecessor screens.
   *
   * Algorithm:
   * 1. BFS from launchScreenId to find all predecessors of sourceStepId
   * 2. Scan FlowDocument.variables for those with boundScreenIds matching predecessors
   * 3. Return variables bound to predecessor screens
   * 4. Return BoundVariable[] with binding source info
   *
   * @param flowDocument - Full FlowDocument with variables and screens
   * @param sourceStepId - The step whose route is being edited
   * @returns BoundVariable[] available for route conditions
   */
  findAvailableVariables(
    flowDocument: FlowDocument,
    sourceStepId: string,
  ): BoundVariable[] {
    const variables = flowDocument.variables ?? {};
    if (Object.keys(variables).length === 0) {
      return [];
    }

    // Find all predecessor step IDs
    const predecessorIds = this.findPredecessorStepIds(flowDocument, sourceStepId);

    // Include the source step itself (its own bindings are available for its outgoing routes)
    const allStepIds = [...predecessorIds, sourceStepId];

    // Collect bound variables — scan variables for boundScreenIds matching predecessors
    // Variable is the single source of truth (schema 2.1.0+)
    const result: BoundVariable[] = [];
    const seenVariableIds = new Set<string>();

    for (const variable of Object.values(variables)) {
      if (seenVariableIds.has(variable.id)) continue;

      const boundScreenIds = (variable as unknown as Record<string, string[]>).boundScreenIds ?? [];
      for (const screenId of boundScreenIds) {
        if (!allStepIds.includes(screenId)) continue;

        seenVariableIds.add(variable.id);
        const step = flowDocument.screens[screenId];
        result.push({
          variable,
          boundByStepId: screenId,
          boundByStepName: step?.name || screenId,
        });
        break; // one entry per variable
      }
    }

    // Second pass: include global variables (scope === 'global').
    // These are variables populated from external sources (URL params, server data)
    // that are visible everywhere regardless of screen binding.
    // Uses the explicit scope field (schema 2.9.2+) instead of fragile boundScreenIds heuristic.
    for (const variable of Object.values(variables)) {
      if (seenVariableIds.has(variable.id)) continue;

      const scope = (variable as unknown as Record<string, unknown>).scope;
      if (scope === 'global') {
        seenVariableIds.add(variable.id);
        result.push({ variable });
      }
    }

    return result;
  }

  /**
   * Find ALL predecessor step IDs that have any forward path to targetStepId.
   *
   * Algorithm:
   * 1. Build forward adjacency from FlowDocument edges
   * 2. Invert to reverse adjacency (stepId → who points to it)
   * 3. BFS backward from targetStepId through reverse edges
   * 4. Intersect with steps reachable from launch (forward BFS) to exclude orphans
   *
   * This finds ALL predecessors across all converging branches, not just one path.
   *
   * @param flowDocument - FlowDocument with screens
   * @param targetStepId - Target step to find predecessors for
   * @returns Array of predecessor step IDs (excludes target itself)
   */
  findPredecessorStepIds(
    flowDocument: FlowDocument,
    targetStepId: string,
  ): string[] {
    const { launchScreenId } = flowDocument;
    if (!launchScreenId) return [];

    const forwardAdj = this.buildForwardAdjacency(flowDocument);

    // Build reverse adjacency: stepId → steps that point TO it
    const reverseAdj = new Map<string, string[]>();
    for (const [stepId, neighbors] of forwardAdj) {
      for (const neighbor of neighbors) {
        const existing = reverseAdj.get(neighbor);
        if (existing) {
          existing.push(stepId);
        } else {
          reverseAdj.set(neighbor, [stepId]);
        }
      }
    }

    // Forward BFS from launch — find all reachable steps
    const reachableFromLaunch = new Set<string>();
    const fwdQueue: string[] = [launchScreenId];
    reachableFromLaunch.add(launchScreenId);
    while (fwdQueue.length > 0) {
      const current = fwdQueue.shift()!;
      for (const neighbor of (forwardAdj.get(current) ?? [])) {
        if (!reachableFromLaunch.has(neighbor)) {
          reachableFromLaunch.add(neighbor);
          fwdQueue.push(neighbor);
        }
      }
    }

    // Reverse BFS from target — find all ancestors
    const ancestors = new Set<string>();
    const revQueue: string[] = [targetStepId];
    ancestors.add(targetStepId);
    while (revQueue.length > 0) {
      const current = revQueue.shift()!;
      for (const pred of (reverseAdj.get(current) ?? [])) {
        if (!ancestors.has(pred)) {
          ancestors.add(pred);
          revQueue.push(pred);
        }
      }
    }

    // Intersect: ancestors that are reachable from launch (exclude orphans + target itself)
    ancestors.delete(targetStepId);
    return Array.from(ancestors).filter((id) => reachableFromLaunch.has(id));
  }

  /**
   * Build forward adjacency list from FlowDocument.
   * stepId → Set of nextStepIds reachable from that step.
   */
  private buildForwardAdjacency(flowDocument: FlowDocument): Map<string, string[]> {
    const adjacency = new Map<string, string[]>();

    for (const [stepId, step] of Object.entries(flowDocument.screens)) {
      const nextIds = this.extractNextStepIds(step);
      adjacency.set(stepId, nextIds);
    }

    return adjacency;
  }

  /**
   * Extract all nextScreenIds from a StepNode's content edges.
   */
  private extractNextStepIds(step: StepNode): string[] {
    const nextIds = new Set<string>();
    const content = step.content as unknown as Record<string, unknown> | undefined;
    if (!content) return [];

    // Walk each edge path
    for (const path of V2_EDGE_PATHS) {
      if (path === 'options.items') {
        // Special: items is an array, each item has action.edges
        this.extractFromOptionItems(content, nextIds);
      } else {
        this.extractFromPath(content, path, nextIds);
      }
    }

    return Array.from(nextIds);
  }

  /**
   * Extract nextScreenIds from a dot-separated path within content.
   * The path leads to an edges array with objects containing nextScreenId.
   */
  private extractFromPath(
    obj: Record<string, unknown>,
    path: string,
    nextIds: Set<string>,
  ): void {
    const parts = path.split('.');
    let current: unknown = obj;

    for (const part of parts) {
      if (current == null || typeof current !== 'object') return;
      current = (current as Record<string, unknown>)[part];
    }

    // current should be an array of edges
    if (!Array.isArray(current)) return;

    for (const edge of current) {
      if (edge && typeof edge === 'object') {
        const edgeObj = edge as Record<string, unknown>;
        // V2 Edge has routes[].nextScreenId OR direct nextScreenId
        if (edgeObj.nextScreenId && typeof edgeObj.nextScreenId === 'string') {
          nextIds.add(edgeObj.nextScreenId);
        }
        // Also check routes array
        if (Array.isArray(edgeObj.routes)) {
          for (const route of edgeObj.routes) {
            if (route && typeof route === 'object') {
              const routeObj = route as Record<string, unknown>;
              if (routeObj.nextScreenId && typeof routeObj.nextScreenId === 'string') {
                nextIds.add(routeObj.nextScreenId);
              }
            }
          }
        }
      }
    }
  }

  /**
   * Extract nextScreenIds from options.items[].action.edges.
   */
  private extractFromOptionItems(
    content: Record<string, unknown>,
    nextIds: Set<string>,
  ): void {
    const options = content.options as Record<string, unknown> | undefined;
    if (!options) return;

    const items = options.items as unknown[] | undefined;
    if (!Array.isArray(items)) return;

    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const itemObj = item as Record<string, unknown>;
      const action = itemObj.action as Record<string, unknown> | undefined;
      if (!action) continue;

      const edges = action.edges as unknown[] | undefined;
      if (!Array.isArray(edges)) continue;

      for (const edge of edges) {
        if (edge && typeof edge === 'object') {
          const edgeObj = edge as Record<string, unknown>;
          if (edgeObj.nextScreenId && typeof edgeObj.nextScreenId === 'string') {
            nextIds.add(edgeObj.nextScreenId);
          }
          if (Array.isArray(edgeObj.routes)) {
            for (const route of edgeObj.routes) {
              if (route && typeof route === 'object') {
                const routeObj = route as Record<string, unknown>;
                if (routeObj.nextScreenId && typeof routeObj.nextScreenId === 'string') {
                  nextIds.add(routeObj.nextScreenId);
                }
              }
            }
          }
        }
      }
    }
  }

}

/** Singleton instance */
export const variableResolver = new VariableResolver();
